import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, mergeMap, of, takeUntil, tap } from 'rxjs';
import { clearAllState } from '../../../core/store/actions/app.actions';
import { closeOnEscape } from '../../../shared/functions/dialog.functions';
import { patientSwitchInitiated } from '../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../user/store/selectors/user.selectors';
import { PharmacySearchDialogComponent } from '../../components/pharmacy-search-dialog/pharmacy-search-dialog.component';
import { PatientPharmaciesService } from '../../services/patient-pharmacies/patient-pharmacies.service';
import { PharmaciesService } from '../../services/pharmacies/pharmacies.service';
import * as actions from '../actions/pharmacy.actions';
import { selectPageNumber } from '../selectors/pharmacy.selector';

@Injectable()
export class PharmacyEffects {
  private actions$ = inject(Actions);
  private patientPharmaciesService = inject(PatientPharmaciesService);
  private pharmaciesService = inject(PharmaciesService);
  private store = inject(Store);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getPatientPharmacies$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPatientPharmacies),
      concatLatestFrom(() => [this.store.select(selectPatientId)]),
      mergeMap(([, patientId]) => {
        return this.patientPharmaciesService.getPatientPharmacies(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(patientPharmacies => {
            return actions.getPatientPharmaciesSuccess({ patientPharmacies });
          }),
          catchError(err => {
            return of(actions.getPatientPharmaciesFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  navigateToSearchResults$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(actions.navigateToSearchResults),
        concatLatestFrom(() => [this.store.select(selectPatientId)]),
        tap(([action, patientId]) => {
          const route = ['patient', patientId, 'pharmacy-search-results', action.medicationId];
          this.router.navigate(route, { queryParams: action.searchParams });
        })
      );
    },
    { dispatch: false }
  );

  getPharmaciesSearch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPharmaciesSearch, actions.nextPage),
      concatLatestFrom(() => [this.store.select(selectPageNumber)]),
      mergeMap(([action, pageNumber]) => {
        const page = pageNumber ? pageNumber : 1;
        return this.pharmaciesService.getPharmaciesSearch(action.searchParams, page).pipe(
          map(pharmacies => {
            return actions.getPharmaciesSearchSuccess({ pharmacies });
          }),
          catchError(err => {
            return of(actions.getPharmaciesSearchFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  getPharmacyById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.getPharmacyById),
      mergeMap(action => {
        return this.pharmaciesService.getPharmacyById(action.pharmacyId).pipe(
          map(pharmacy => {
            return actions.getPharmacyByIdSuccess({ pharmacy });
          }),
          catchError(err => {
            return of(actions.getPharmacyByIdFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  navigateToPatientPharmacies$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(actions.navigateToPatientPharmacies),
        concatLatestFrom(() => [this.store.select(selectPatientId)]),
        tap(([action, patientId]) => {
          const route = ['patient', patientId, 'patient-pharmacies', action.medicationId];
          this.router.navigate(route, { queryParams: { pharmacyId: action.pharmacyId } });
        })
      );
    },
    { dispatch: false }
  );

  openPharmacySearchDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.openPharmacySearchDialog),
      exhaustMap(() => {
        const dialogRef = this.dialog.open(PharmacySearchDialogComponent, {
          id: 'pharmacySearchDialog',
          disableClose: true,
          autoFocus: false,
          width: 'min(1120px, calc(100vw - 48px))',
          minWidth: '960px',
          height: 'min(820px, calc(100dvh - 40px))',
          minHeight: '520px',
          maxHeight: 'min(820px, calc(100dvh - 40px))',
          panelClass: 'pharmacy-search-dialog',
        });
        closeOnEscape(dialogRef);

        return dialogRef
          .afterClosed()
          .pipe(
            map(result =>
              result
                ? actions.closePharmacySearchDialog({ selectedPharmacy: result })
                : actions.cancelPharmacySearchDialog()
            )
          );
      })
    );
  });
}
